<template>
  <div class="mutual-action-plan layout-container">
    <div class="layout-content">
      <div v-for="actionPlan in actionPlans" :key="actionPlan.id" class="action-plan">
        <div id="mutual-action-plan-title-container" class="action-plan-info-container">
          <FriedH2 class="map-title paragraph-large">{{ actionPlan.title }}</FriedH2>
          <InlineRichTextEditorViewer
            :content="formattedDescription(actionPlan.description)"
            class="description"
            data-external="map-description-text"
          />
          <div class="button-container">
            <FriedButton
              v-if="completedMilestones(actionPlan).length > 0 && !isXSmallScreenAndDown"
              :button-type="ButtonType.Tertiary"
              data-external="show-completed-milestones-button"
              @click="toggleCompleted"
            >
              {{
                showCompletedMilestones
                  ? t('MAP_hide_completed_milestones')
                  : t('MAP_show_completed_milestones')
              }}
              <template #rightIcon>
                <FriedIcon :icon="showCompletedMilestones ? 'view-hide' : 'view'" />
              </template>
            </FriedButton>
          </div>
        </div>
        <template v-if="!loadingMilestones">
          <div v-if="isActionPlanCompleted(actionPlan)" class="completed-action-plan-content">
            <FriedH3>{{ t('map_action_plan_completed') }}</FriedH3>
            <div class="check-mark-container">
              <FriedIcon :size="IconSize.Small" icon="bold-check-mark" />
            </div>
          </div>
          <Overview
            :active-milestone="activeMilestone"
            :action-plan="actionPlan"
            :show-completed="showCompletedMilestones"
            @set-active-milestone="setActiveMilestone"
          />
          <Milestone
            v-for="milestone in filterMilestones(actionPlan.milestones)"
            :key="milestone.id"
            :milestone="milestone"
            :action-plan-id="actionPlan.id"
            :milestone-order="getMilestoneOrder(milestone.id, actionPlan.milestones)"
            :participant-id="participantId"
            :participants="participants"
            :class="{ 'active-milestone': activeMilestone === milestone.id }"
            :owner="owner"
            @update-task-status="handleUpdateTaskStatus(actionPlan.id, milestone.id, $event)"
          />
        </template>
        <LoadMore
          v-if="hasMilestones(actionPlan)"
          :loading="loadingMore"
          class="load-more"
          @load="loadMoreMilestones(actionPlan)"
        >
          <FriedSpinner />
        </LoadMore>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import Overview from '@getaccept/dsr-shared-new/src/mutual-action-plan/components/Overview.vue';
import { useI18n } from 'vue-i18n';
import type { PropType } from 'vue';
import { storeToRefs } from 'pinia';
import type { TaskStatus } from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/enums/task-status';
import { getDescriptionContent } from '@getaccept/editor-lib-new';
import InlineRichTextEditorViewer from '@getaccept/dsr-shared-new/src/inline-text-editor/components/InlineRichTextEditorViewer.vue';
import { ButtonType, IconSize } from '@getaccept/fried-tofu';
import { useActionPlan } from '@getaccept/dsr-shared-new/src/mutual-action-plan/composables/action-plan.composable';
import { MilestoneHelper } from '@getaccept/dsr-shared-new/src/mutual-action-plan/helpers/milestone.helper';
import type { Participant } from '@getaccept/dsr-shared-new';
import MilestoneConstants from '@getaccept/dsr-shared-new/src/api/mutual-action-plan/constants/load-milestone.constant';
import type { ActionPlan } from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/action-plan';
import LoadMore from '@getaccept/dsr-shared-new/src/components/LoadMore.vue';
import useBreakpoints from '@getaccept/lib-shared-new/src/composables/breakpoints.composable';
import { useParticipantsStore } from '../../store/participants.store';
import { usePublishedActionPlanStore } from '../store/action-plan.store';
import Milestone from './Milestone.vue';

export default defineComponent({
  components: {
    Overview,
    Milestone,
    InlineRichTextEditorViewer,
    LoadMore,
  },
  props: {
    owner: {
      type: Object as PropType<Participant>,
      required: true,
    },
    participantId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const store = usePublishedActionPlanStore();
    const { actionPlans, loadingMilestones, loadingMore } = storeToRefs(store);
    const { participants } = useParticipantsStore();
    const {
      showCompletedMilestones,
      activeMilestone,
      isActionPlanCompleted,
      setActiveMilestone,
      hasMilestones,
      filterMilestones,
      toggleCompleted,
      getMilestoneOrder,
    } = useActionPlan();

    const formattedDescription = (text: string) => getDescriptionContent(text);

    const handleUpdateTaskStatus = (
      actionPlanId: string,
      milestoneId: string,
      { status, taskId }: { status: TaskStatus; taskId: string }
    ) => {
      store.updateTaskStatus(actionPlanId, milestoneId, taskId, status, props.participantId);
    };

    const loadMoreMilestones = (actionPlan: ActionPlan) => {
      store.loadMilestones({
        ...MilestoneConstants.defaultFilter,
        offset: actionPlan.milestones.length,
        actionPlanId: actionPlan.id,
      });
    };

    const { isXSmallScreenAndDown } = useBreakpoints();

    return {
      actionPlans,
      loadingMilestones,
      participants,
      showCompletedMilestones,
      loadingMore,
      activeMilestone,
      isActionPlanCompleted,
      setActiveMilestone,
      filterMilestones,
      toggleCompleted,
      hasMilestones,
      loadMoreMilestones,
      getMilestoneOrder,
      formattedDescription,
      completedMilestones: MilestoneHelper.completedMilestones,
      isXSmallScreenAndDown,

      handleUpdateTaskStatus,
      t,
      ButtonType,
      IconSize,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/layout';
@import '@getaccept/dsr-shared-new/src/scss/action-plan';
@import '@getaccept/dsr-shared-new/src/scss/milestone';
</style>
